/* eslint-disable @typescript-eslint/no-explicit-any */
import { Value } from 'node_modules/react-calendar/dist/esm/shared/types';

export const formatDateToDDMMYYYY = (dateStr: string) => {
    if (!dateStr) {
        return '';
    }

    const date = new Date(dateStr);

    if (date instanceof Date) {
        return date.toLocaleDateString('ru-RU').replace(/\//g, '.'); // заменяет / на .
    }

    return dateStr;
};

// Вспомогательная функция для форматирования даты в "MM-DD"
function formatDate(date: Date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${month}-${day}`;
}

// Вспомогательная функция для форматирования даты в "YYYY-MM-DD"
function formatFullDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function isTodayAndAfter4pm() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();

    return hours > 16 || (hours === 16 && minutes > 0) || (hours === 16 && seconds > 0);
}

export function getWeekendsDates() {
    const year = new Date().getFullYear();
    const weekends = [];

    // Праздничные дни РФ (фиксированные даты)
    const fixedHolidays = [
        `01-01`, // Новый год
        `01-02`,
        `01-03`,
        `01-04`,
        `01-05`,
        `01-06`,
        `01-07`, // Рождество
        `01-08`,
        `02-23`, // День защитника Отечества
        `03-08`, // Международный женский день
        `05-01`, // Праздник весны и труда
        `05-09`, // День Победы
        `06-12`, // День России
        `11-04`, // День народного единства
    ];

    // Праздничные дни РФ с плавающей датой (например, в 2024 году 1 мая выпало на среду)
    // Можно добавить вручную или использовать API производственного календаря
    const floatingHolidays = [
        // Пример для 2024 года (можно расширить)
        `2024-05-01`, // Праздник весны и труда (среда)
        `2024-05-09`, // День Победы (четверг)
    ];

    for (let month = 0; month < 12; month++) {
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayOfWeek = date.getDay();
            const dateStr = formatDate(date); // Форматируем дату в "MM-DD"

            // Проверяем, является ли день выходным (суббота или воскресенье)
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            // Проверяем, является ли день праздником (фиксированным или плавающим)
            const isFixedHoliday = fixedHolidays.includes(dateStr);
            const isFloatingHoliday = floatingHolidays.includes(formatFullDate(date));

            if (isWeekend || isFixedHoliday || isFloatingHoliday) {
                weekends.push(new Date(date));
            }
        }
    }

    return weekends;
}

// преобразовывает из DD.MM.YYYY в YYYY-MM-DD;
export const prepareDate = (dateStr: string) => {
    const [day, month, year] = dateStr.split('.');

    return `${year}-${month}-${day}`;
};

export const getDateFormat = (date: undefined | Value) => {
    return date?.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(' г.', '').toString();
};

export const isValidDate = (dateString: string) => {
    // Проверяем формат "DD.MM.YYYY" (но не валидирует саму дату, например, 99.99.9999 пройдет)
    const datePattern = /^\d{2}\.\d{2}\.\d{4}$/;

    return datePattern.test(dateString);
};

// Функция для вычисления возраста
export const calculateAge = (birthDate: Date, onDate: Date): number => {
    if (birthDate) {
        let age = onDate.getFullYear() - birthDate.getFullYear();
        const monthDifference = onDate.getMonth() - birthDate.getMonth();
        const dayDifference = onDate.getDate() - birthDate.getDate();

        // Если текущий месяц меньше месяца рождения,
        // или текущий месяц равен месяцу рождения, но текущий день меньше дня рождения,
        // уменьшаем возраст на 1
        if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
            age -= 1;
        }

        return age;
    }

    return NaN;
};

export const isoDate = (UTCDate: Date | string) => {
    const date = new Date(UTCDate);

    // Форматируем дату и время в нужный формат
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Месяцы в JavaScript от 0 до 11
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    // Собираем строку в нужном формате
    const localDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;

    return localDateString;
};

export const getValidateDateFormat = (dateString: string) => {
    // Разделение строки на составляющие
    const [day, month, year] = dateString.split('.').map(Number);

    // Создание объекта Date (месяцы в JavaScript начинаются с 0)
    const date = new Date(year, month - 1, day);

    // Преобразование даты в нужный формат
    return date;
};

export const localDate = (date: null | string | undefined) => (date ? new Date(date).toLocaleDateString('ru-RU') : null);

export const getTimeFormat = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export function DifferinceYears(startDate: any, endDate: any) {
    // Преобразуем строки в объекты Date
    if (startDate instanceof Date && endDate instanceof Date) {
        startDate = new Date(startDate);
        endDate = new Date(endDate);

        // Вычисляем разницу в годах
        let yearsDifference = endDate.getFullYear() - startDate.getFullYear();

        // Проверяем, прошёл startDate в конечном году
        const isBeforeStartDay =
            endDate.getMonth() < startDate.getMonth() ||
            (endDate.getMonth() === startDate.getMonth() && endDate.getDate() < startDate.getDate());

        // Если startDate еще не прошел в этом году, уменьшаем разницу на 1
        if (isBeforeStartDay) {
            yearsDifference--;
        }

        // Проверяем, если разница лет больше 14
        return yearsDifference;
    }
}

export function replaceTimeWithMidnight(currentDate: Date) {
    const dateString = currentDate.toString().replace(/\d{2}:\d{2}:\d{2}/, '00:00:00');
    const date = new Date(dateString);

    return date;
}

export function isAtLeastTomorrow(date: Date | string) {
    if (!(date instanceof Date) || !date) {
        return false;
    }
    const inputDate = new Date(date);

    inputDate.setHours(0, 0, 0, 0);

    const currentDate = new Date();

    currentDate.setHours(0, 0, 0, 0);

    return inputDate >= currentDate;
}

export function isWeekday(date: Date | string) {
    if (!(date instanceof Date) || !date) {
        return false;
    }
    const day = date.getDay(); // 0 — воскресенье, 6 — суббота

    return day >= 1 && day <= 5;
}

export function getTomorrowDate(): Date {
    const today = new Date();
    const tomorrow = new Date(today);

    tomorrow.setDate(today.getDate() + 1);

    return tomorrow;
}

export function getYesterdayDate(): Date {
    const today = new Date();
    const yesterday = new Date(today);

    yesterday.setDate(today.getDate() - 1);

    return yesterday;
}

export function isToday(dateStr: string): boolean {
    if (!dateStr) {
        return false;
    }

    const [day, month, year] = dateStr.split('.');
    const inputDate = new Date(`${year}-${month}-${day}`);

    const today = new Date();

    return (
        inputDate.getDate() === today.getDate() &&
        inputDate.getMonth() === today.getMonth() &&
        inputDate.getFullYear() === today.getFullYear()
    );
}

export function parseDirtyDateString(dirtyDateString: string): Date | string {
    if (!dirtyDateString) {
        return dirtyDateString;
    }

    const numbers = dirtyDateString.match(/\d+/g);

    if (!numbers || numbers.length < 3) {
        return dirtyDateString;
    }

    const [day, month, year] = numbers.map(Number);
    const date = new Date(year, month - 1, day);

    const isDateValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    if (!isDateValid) {
        return dirtyDateString;
    }

    return date;
}
